import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { cn } from '../utils/cn';
import ChangePassword from './ChangePassword';

// Temporary icon components until lucide-react is resolved
const Icon = ({ children, className = "h-5 w-5" }) => (
  <span className={className}>{children}</span>
);

const Home = () => <Icon>🏠</Icon>;
const Calendar = () => <Icon>📅</Icon>;
const Users = () => <Icon>👥</Icon>;
const DollarSign = () => <Icon>💰</Icon>;
const BarChart3 = () => <Icon>📊</Icon>;
const PieChart = () => <Icon>📈</Icon>;
const Target = () => <Icon>🎯</Icon>;
const Settings = () => <Icon>⚙️</Icon>;
const Shield = () => <Icon>🛡️</Icon>;
const Menu = () => <Icon>☰</Icon>;
const X = () => <Icon>✕</Icon>;
const Compensation = () => <Icon>💼</Icon>;

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userInfo, setUserInfo] = useState({ username: 'User', email: 'user@storytimechess.com' });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Tutors', href: '/tutors', icon: Users },
    { name: 'Payroll', href: '/payroll', icon: DollarSign },
    { name: 'Analytics', href: '/analytics', icon: BarChart3 },
    { name: 'Profit Share', href: '/profit-share', icon: PieChart },
    // { name: 'Compensation Plan', href: '/compensation-plan', icon: Compensation },
    { name: 'Lead Tracking', href: '/leads', icon: Target },
    { name: 'Settings', href: '/settings', icon: Settings },
    { name: 'Admin Panel', href: '/adminpanel', icon: Shield },
  ];

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  // Get user info from JWT token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserInfo({
          username: decoded.username || 'User',
          email: decoded.email || decoded.username || 'user@storytimechess.com'
        });
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }, []);

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          style={{ zIndex: 40 }}
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )} style={{ zIndex: 50 }}>
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-6 border-b border-gray-200">
            <div className="flex items-center space-x-3 min-w-0">
              <img 
                src="/logo.png" 
                alt="Story Time Chess Logo" 
                className="w-8 h-8 object-contain flex-shrink-0"
              />
              <span className="text-xl font-bold text-gray-900 truncate">Story Time Chess</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex-shrink-0"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors min-w-0",
                    isActive(item.href)
                      ? "bg-primary text-white shadow-sm"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span className="truncate">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="border-t border-gray-200 p-4 flex-shrink-0">
            <div className="flex items-center space-x-3 mb-3 min-w-0">
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-gray-600 font-medium text-sm">U</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{userInfo.username}</p>
                <p className="text-xs text-gray-500 truncate">{userInfo.email}</p>
              </div>
            </div>
            <button
              onClick={() => setShowChangePassword(true)}
              className="w-full text-left text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 p-2 rounded transition-colors"
            >
              🔐 Change Password
            </button>
          </div>

          {/* Logout section */}
          <div className="border-t border-gray-200 p-4 flex-shrink-0">
            <button
              onClick={() => {
                localStorage.removeItem('token');
                window.location.href = '/login';
              }}
              className="flex items-center space-x-3 w-full p-2 rounded-lg hover:bg-gray-100 transition-colors text-left"
            >
              <div className="w-5 h-5 text-gray-500 flex-shrink-0">
                <span>🚪</span>
              </div>
              <span className="text-sm font-medium text-gray-700">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:pl-0 overflow-hidden">
        {/* Mobile menu button */}
        <div className="lg:hidden bg-white border-b border-gray-200 flex-shrink-0">
          <div className="flex h-12 items-center px-4 min-w-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex-shrink-0"
            >
              <Menu className="h-6 w-6" />
            </button>
            <span className="ml-3 text-lg font-semibold text-gray-900 truncate">Story Time Chess</span>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto min-w-0">
          <div className="overflow-x-auto min-w-0">
            {children}
          </div>
        </main>
      </div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePassword onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
};

export default Layout;
